import { useMemo, useState } from "react";
import { api } from "../api.js";
import { Badge, Button, EmptyState, Field, LoadingRows, Notice, PageHeader } from "../components/ui.jsx";

function formatDate(value) {
  if (!value) return "Not observed";
  return new Intl.DateTimeFormat("en", { dateStyle: "medium" }).format(new Date(value));
}

function formatValue(value) {
  if (value == null || value === "") return "Not available";
  if (typeof value === "number") return new Intl.NumberFormat("en").format(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export default function CatalogPage() {
  const [query, setQuery] = useState("");
  const [state, setState] = useState({ status: "idle", companies: [], error: "" });
  const columns = useMemo(() => {
    const known = new Map();
    for (const company of state.companies) {
      for (const field of company.fields || []) known.set(field.key, { key: field.key, label: field.label });
    }
    return [...known.values()].sort((left, right) => left.label.localeCompare(right.label));
  }, [state.companies]);

  async function search(event) {
    event.preventDefault();
    const normalized = query.trim();
    if (!normalized) return;
    setState((current) => ({ ...current, status: "loading", error: "" }));
    try {
      const data = await api.catalogCompanies(normalized, 50);
      setState({ status: "ready", companies: data.companies, error: "" });
    } catch (error) {
      setState({ status: "ready", companies: [], error: error.message });
    }
  }

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="RevPal shared data"
        title="Company catalog"
        description="Search canonical companies and reuse fresh, sourced values collected across RevPal. Workspace notes, lists, and outreach remain private."
      />
      <form className="flex max-w-2xl flex-col gap-3 sm:flex-row sm:items-end" onSubmit={search}>
        <div className="min-w-0 flex-1">
          <Field
            name="catalog_query"
            label="Company name or domain"
            placeholder="Flipkart or flipkart.com"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
            required
          />
        </div>
        <Button className="sm:min-h-[38px]" variant="primary" type="submit" disabled={state.status === "loading"}>
          {state.status === "loading" ? "Searching…" : "Search catalog"}
        </Button>
      </form>
      {state.error && <Notice>{state.error}</Notice>}
      {state.status === "loading" ? <LoadingRows count={4} /> : state.status === "idle" ? (
        <EmptyState title="Search the shared catalog" description="RevPal matches names, known aliases, and company domains to one canonical company record." />
      ) : !state.companies.length ? (
        <EmptyState title="No catalog company found" description="The company can be created in your workspace and linked to the catalog when a valid domain is available." />
      ) : (
        <section aria-label="Catalog search results">
          <div className="overflow-x-auto whitespace-nowrap rounded-xl bg-card shadow-soft">
            <table className="w-full border-collapse text-left text-[13px]">
              <thead>
                <tr className="h-10 bg-muted/50 text-[11px] uppercase tracking-[.06em] text-muted-foreground">
                  <th className="whitespace-nowrap border-b border-border border-r border-r-neutral-100 px-3 font-semibold">Company</th>
                  <th className="whitespace-nowrap border-b border-border border-r border-r-neutral-100 px-3 font-semibold">Domain</th>
                  {columns.map((column) => <th className="whitespace-nowrap border-b border-border border-r border-r-neutral-100 px-3 font-semibold" key={column.key}>{column.label}</th>)}
                  <th className="whitespace-nowrap border-b border-border px-3 font-semibold">Last catalog update</th>
                </tr>
              </thead>
              <tbody>{state.companies.map((company) => {
                const fields = new Map((company.fields || []).map((field) => [field.key, field]));
                const observed = (company.fields || []).map((field) => field.current?.observed_at).filter(Boolean).sort().at(-1);
                return (
                  <tr className="border-b border-neutral-100 transition-colors last:border-b-0 hover:bg-muted/50" key={company.id}>
                    <td className="border-r border-neutral-100 px-3 py-2.5 font-medium text-foreground">{company.name}</td>
                    <td className="border-r border-neutral-100 px-3 py-2.5 text-muted-foreground">{company.domain}</td>
                    {columns.map((column) => {
                      const field = fields.get(column.key);
                      return <td className="border-r border-neutral-100 px-3 py-2.5 text-neutral-600" key={column.key}>{field ? <span className="flex items-center gap-2"><span className="tabular-nums">{formatValue(field.current?.value)}</span>{field.current?.freshness === "stale" && <Badge status="stale">stale</Badge>}</span> : <span className="text-muted-foreground">Not available</span>}</td>;
                    })}
                    <td className="px-3 py-2.5 tabular-nums text-muted-foreground">{formatDate(observed)}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
